import { alpha, Box, Chip, Typography } from "@mui/material";
import dayjs from "dayjs";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { quoteModern } from "../style/style";

const clean = (value?: any) => String(value ?? "").trim();

const formatDate = (value?: any) => {
  if (!value) return dayjs().format("MMM DD, YYYY");
  return dayjs(value).format("MMM DD, YYYY");
};

const formatText = (value?: any) => {
  const normalized = clean(value);

  if (!normalized) return "";

  const map: Record<string, string> = {
    approved: "Approved",
    pending: "Pending",
    rejected: "Rejected",
    draft: "Draft",
    basic: "Basic quote",
    advanced: "Advanced quote",
  };

  const key = normalized.toLowerCase();

  if (map[key]) return map[key];

  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const getQuoteStatus = (quote: any) => {
  return clean(quote?.status) || clean(quote?.quoteStatus) || (quote?.isApproved ? "Approved" : "") || "Pending";
};

const getQuoteType = (quote: any) => {
  return clean(quote?.variant) || clean(quote?.type) || clean(quote?.quoteType) || "";
};

const HeaderInfoItem = ({ icon, label, value }) => {
  if (!clean(value)) return null;

  return (
    <Box
      sx={{
        p: 1.15,
        borderRadius: 2,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        border: `1px solid ${quoteModern.colors.line}`,
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1.5,
          bgcolor: quoteModern.colors.orangeSoft,
          color: quoteModern.colors.orange,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={quoteModern.labelSx}>{label}</Typography>

        <Typography
          sx={{
            mt: 0.25,
            fontSize: 12.5,
            fontWeight: 800,
            color: quoteModern.colors.textPrimary,
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={value}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

export const QuoteHeader = ({ quote }) => {
  const quoteId = quote?.id ?? quote?.uid ?? "-";
  const quoteAlias = clean(quote?.alias);
  const formattedDate = formatDate(quote?.createdAt);
  const status = formatText(getQuoteStatus(quote));
  const quoteType = formatText(getQuoteType(quote));

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 3,
        bgcolor: quoteModern.colors.sheetBg,
        border: `1px solid ${quoteModern.colors.line}`,
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        alignItems: { xs: "flex-start", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2.25,
            bgcolor: quoteModern.colors.orange,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: `0 10px 22px ${alpha(quoteModern.colors.orange, 0.22)}`,
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 24 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={quoteModern.labelSx}>Estimate document</Typography>

          <Box
            sx={{
              mt: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 22, sm: 26 },
                fontWeight: 900,
                color: quoteModern.colors.textPrimary,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              Estimate
            </Typography>
          </Box>

          {quoteAlias && (
            <Typography
              title={quoteAlias}
              sx={{
                minWidth: 0,
                maxWidth: { xs: 150, sm: 260 },
                fontSize: { xs: 15, sm: 17 },
                fontWeight: 800,
                color: quoteModern.colors.textSecondary,
                lineHeight: 1.1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {quoteAlias}
            </Typography>
          )}
          <Box
            sx={{
              mt: 1,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
            }}
          >
            <Chip
              label={`EST-${quoteId}`}
              size="small"
              sx={{
                height: 24,
                borderRadius: 999,
                bgcolor: quoteModern.colors.orange,
                color: "#FFFFFF",
                fontWeight: 900,
                fontSize: 11,
              }}
            />

            <Chip
              label={status}
              size="small"
              sx={{
                height: 24,
                borderRadius: 999,
                bgcolor: quoteModern.colors.orangeSoft,
                color: quoteModern.colors.orange,
                fontWeight: 900,
                fontSize: 11,
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: { xs: "100%", sm: "auto" },
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: quoteType ? "1fr 1fr" : "1fr",
          },
          gap: 1,
          minWidth: { sm: 280 },
        }}
      >
        <HeaderInfoItem icon={<EventOutlinedIcon sx={{ fontSize: 17 }} />} label="Issued" value={formattedDate} />

        {quoteType && <HeaderInfoItem icon={<CheckCircleOutlineOutlinedIcon sx={{ fontSize: 17 }} />} label="Quote type" value={quoteType} />}

        {!quoteType && <HeaderInfoItem icon={<NumbersOutlinedIcon sx={{ fontSize: 17 }} />} label="Reference" value={`EST-${quoteId}`} />}
      </Box>
    </Box>
  );
};
